import { useState, useEffect, useMemo, useCallback } from 'react';
import { devicesApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tag, Download, Printer, Loader2, CheckSquare } from 'lucide-react';

const isEligible = (d) =>
  d && d.is_active !== false
  && (!String(d.device_eui || '').startsWith('pending_') || d.is_placeholder)
  && !d.is_test && d.environment !== 'test' && d.environment !== 'demo';

export function PackagingLabelsDialog({ open, onOpenChange, schools = [], devices = [] }) {
  const { isSuperAdmin, isKommunAdmin } = useAuth();
  const tenantWide = isSuperAdmin() || isKommunAdmin();

  const [schoolId, setSchoolId] = useState('');
  const [sizes, setSizes] = useState([]);
  const [size, setSize] = useState('70x50');
  const [selected, setSelected] = useState([]); // device ids
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const schoolName = schools.find(s => s.id === schoolId)?.name;

  const eligible = useMemo(() => {
    let list = devices.filter(isEligible);
    if (schoolName) list = list.filter(d => d.school_name === schoolName);
    return list.sort((a, b) => (a.room_name || '').localeCompare(b.room_name || '', 'sv'));
  }, [devices, schoolName]);

  useEffect(() => {
    if (!open) return;
    setSize('70x50'); setSelected([]); setPreviewUrl('');
    if (!tenantWide && schools.length >= 1) setSchoolId(schools[0].id);
    else setSchoolId('');
    if (sizes.length === 0) {
      devicesApi.labelSizes().then(r => { setSizes(r.data.sizes || []); setSize(r.data.default || '70x50'); }).catch(() => {});
    }
  }, [open]); // eslint-disable-line

  // Auto-select all eligible when school changes
  useEffect(() => { setSelected(eligible.map(d => d.id)); }, [schoolId, eligible.length]); // eslint-disable-line

  const loadPreview = useCallback(async () => {
    if (!schoolId || selected.length === 0) { setPreviewUrl(''); return; }
    setPreviewLoading(true);
    try {
      const res = await devicesApi.packagingPreview({ school_id: schoolId, size, device_id: selected[0] });
      setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch {
      setPreviewUrl('');
    } finally { setPreviewLoading(false); }
  }, [schoolId, size, selected]);

  useEffect(() => { if (open) loadPreview(); }, [open, loadPreview]);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allSelected = eligible.length > 0 && selected.length === eligible.length;
  const toggleAll = () => setSelected(allSelected ? [] : eligible.map(d => d.id));

  const buildBlob = async () => {
    const res = await devicesApi.packagingLabels({ school_id: schoolId, device_ids: selected, size });
    return res.data;
  };

  const handleExport = async () => {
    if (!schoolId || selected.length === 0) { toast.error('Välj skola och minst en sensor'); return; }
    setBusy(true);
    try {
      const blob = await buildBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Vapelarm_Forpackningsetiketter_${(schoolName || 'skola').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${selected.length} etiketter exporterade`);
    } catch (err) {
      let detail = 'Kunde inte generera etiketter';
      if (err.response?.data instanceof Blob) { try { detail = JSON.parse(await err.response.data.text()).detail || detail; } catch { /* noop */ } }
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const handlePrint = async () => {
    if (!schoolId || selected.length === 0) { toast.error('Välj skola och minst en sensor'); return; }
    setBusy(true);
    try {
      const blob = await buildBlob();
      const url = URL.createObjectURL(blob);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed'; iframe.style.right = '0'; iframe.style.bottom = '0';
      iframe.style.width = '0'; iframe.style.height = '0'; iframe.style.border = '0';
      iframe.src = url;
      iframe.onload = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { window.open(url, '_blank'); } };
      document.body.appendChild(iframe);
      toast.success('Förbereder utskrift…');
    } catch {
      toast.error('Kunde inte förbereda utskrift');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="packaging-labels-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo flex items-center gap-2">
            <Tag className="h-5 w-5 text-navy" /> Förpackningsetiketter
          </DialogTitle>
          <DialogDescription className="font-ibm">
            Skriv ut professionella etiketter för sensorernas förpackningar inför leverans.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-5 py-1">
          {/* Left: controls */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Skola</Label>
              {tenantWide ? (
                <Select value={schoolId} onValueChange={setSchoolId}>
                  <SelectTrigger data-testid="label-school-select"><SelectValue placeholder="Välj skola…" /></SelectTrigger>
                  <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <div className="h-10 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm font-ibm text-slate-700" data-testid="label-school-static">{schoolName || '—'}</div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Etikettstorlek</Label>
              <Select value={size} onValueChange={setSize}>
                <SelectTrigger data-testid="label-size-select"><SelectValue /></SelectTrigger>
                <SelectContent>{sizes.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Sensorer ({selected.length}/{eligible.length})</Label>
                <button onClick={toggleAll} className="text-xs text-navy font-ibm flex items-center gap-1 hover:underline" data-testid="label-select-all">
                  <CheckSquare className="h-3.5 w-3.5" />{allSelected ? 'Avmarkera alla' : 'Markera alla'}
                </button>
              </div>
              <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto divide-y divide-slate-100">
                {eligible.length === 0 ? (
                  <p className="text-sm text-slate-400 font-ibm p-3">Inga aktiva produktionssensorer för vald skola.</p>
                ) : eligible.map(d => (
                  <label key={d.id} className="flex items-center gap-2 px-3 py-2 text-sm font-ibm cursor-pointer hover:bg-slate-50" data-testid={`label-device-${d.id}`}>
                    <Checkbox checked={selected.includes(d.id)} onCheckedChange={() => toggle(d.id)} />
                    <span className="truncate flex-1"><span className="text-slate-800">{d.room_name || d.name}</span></span>
                    <span className="font-mono text-[11px] text-slate-400">{(d.device_eui || '').slice(-10)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Right: live preview */}
          <div className="space-y-1.5">
            <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Förhandsgranskning</Label>
            <div className="border border-slate-200 rounded-md bg-slate-50 min-h-[220px] flex items-center justify-center p-4" data-testid="label-preview">
              {previewLoading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                : previewUrl ? <img src={previewUrl} alt="Etikett förhandsgranskning" className="max-w-full max-h-[320px] shadow-sm rounded-sm" />
                : <p className="text-sm text-slate-400 font-ibm text-center">Välj skola och sensor för att se etiketten.</p>}
            </div>
            <p className="text-[11px] text-slate-400 font-ibm">Visar den första valda sensorn. QR-koden länkar till sensorns servicesida.</p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="font-ibm sm:mr-auto">Avbryt</Button>
          <Button variant="outline" onClick={handlePrint} disabled={busy || selected.length === 0} className="font-ibm gap-2" data-testid="label-print-btn">
            <Printer className="h-4 w-4" /> Skriv ut
          </Button>
          <Button onClick={handleExport} disabled={busy || selected.length === 0} className="bg-navy hover:bg-navy/90 font-ibm gap-2" data-testid="label-export-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Exportera {selected.length > 0 ? `(${selected.length})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
